import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  ToDo,
} from 'types/toDo';
import Table from 'core/components/VirtualTable';
import columns from 'pages/Dashboard/pages/Todos/Overview/columns';
import {
  toDosEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  ListQueryProps,
  TodoDataParams,
} from 'pages/Dashboard/pages/Todos/types';
import {
  buildToDoListRequest,
  getToDosData,
} from 'pages/Dashboard/pages/Todos/transport';
import {
  useMedia,
  useSidePanel,
} from 'core/hooks';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import preserveReadToDoPositions from 'pages/Dashboard/pages/Todos/Overview/preserveReadToDoPositions';

export default function QueryTable({
  dateRange,
  filterQuery = {},
  pillFilters,
  tableInstanceRef,
  searchQuery,
  sortRequest,
}: ListQueryProps) {
  const { requestMedia } = useMedia();
  const { openPanel } = useSidePanel();
  const userInfo = useUserInfo();
  const displayedToDosRef = useRef<ToDo[]>([]);
  const locallyReadToDoIdsRef = useRef<Set<number>>(new Set());
  const queryKeyRef = useRef<string | undefined>(undefined);
  const [locallyReadToDoIds, setLocallyReadToDoIds] = useState<Set<number>>(new Set());

  const getStableToDosData = useCallback(async (query: TodoDataParams) => {
    const queryKey = JSON.stringify(buildToDoListRequest(query));
    const refreshedToDos = await getToDosData(query);
    const queryChanged = queryKeyRef.current !== queryKey;

    if (queryChanged) {
      queryKeyRef.current = queryKey;
      locallyReadToDoIdsRef.current = new Set();
      displayedToDosRef.current = refreshedToDos;
      return refreshedToDos;
    }

    const displayedToDos = locallyReadToDoIdsRef.current.size > 0
      ? preserveReadToDoPositions(
        displayedToDosRef.current,
        refreshedToDos,
        locallyReadToDoIdsRef.current,
      )
      : refreshedToDos;

    displayedToDosRef.current = displayedToDos;
    return displayedToDos;
  }, []);

  useEffect(() => {
    const unsubscribeFromChange = toDosEventChannel.on('onToDosChanged', () => {
      tableInstanceRef?.current?.refetch?.();
    });

    return () => {
      unsubscribeFromChange();
    };
  }, []);

  return (
    <Table<ToDo>
      key={JSON.stringify(sortRequest ?? {})}
      instanceRef={tableInstanceRef}
      getData={getStableToDosData}
      getRowId={(row) => row.toDoId}
      columns={columns}
      classes={{
        header: 'px-4',
        scrollContainer: 'justify-center items-center',
        outerRow: '!w-[calc(100%-16px)] left-4',
        row: (row) => clsx({ 'bg-blue-50': (
          !row.original?.isRead
          && !locallyReadToDoIds.has(row.original.toDoId)
          && row.original?.assignee?.userId === userInfo?.user?.userId
        ) }),
      }}
      queryParams={{
        filterQuery,
        pillFilters,
        dateRange,
        requestMedia,
        searchQuery,
        sortRequest,
      }}
      onRowClick={(table, row) => {
        const toDoId = Number(row.id);
        const shouldMarkAsRead = !row.original.isRead
          && row.original.assignee?.userId === userInfo?.user?.userId;

        if (shouldMarkAsRead) {
          locallyReadToDoIdsRef.current = new Set([
            ...locallyReadToDoIdsRef.current,
            toDoId,
          ]);
          setLocallyReadToDoIds(new Set(locallyReadToDoIdsRef.current));
        }

        openPanel('todo', { todoId: toDoId });
      }}
      defaultPagination={{
        pageSize: 25,
        pageIndex: 0,
      }}
      loadingSlot="Loading To-dos..."
    />
  );
}
